import {
  AccountInfoParamsV2,
  LinkTemplate,
  Notification,
  PlacementType,
  SearchParams,
  SortMode,
  SortOrder,
  TextTemplate,
  TitleHeaderTemplate,
  useNotifications,
  useNotificationsV2,
} from '@amzn/aces-communications-api';
import { UseAcesBannersMetricsPublisher } from '../../../../commons/utils/metrics';
import { logger } from '../../../../commons/utils/logger';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectChromeConfiguration } from '../../../../app/slice';
import { LocalNotification, UseAcesBannersResponse } from './types';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { CONSOLE_NOTIFICATIONS_API_CLIENT_ID } from 'src/commons/constants';

const BANNER_SEARCH_PARAMS = {
  page: { size: 10, current: 0 },
  sort: { sortBy: SortMode?.MESSAGE_TYPE, sortDirection: SortOrder?.DESC },
  filter: {},
};

const SUPPORTED_ACES_MSG_TYPES = {
  INFORMATION: 'info',
  WARNING: 'warning',
  ERROR: 'error',
};

// Raw actions returned by the aces notifications hooks. Mirrors the banner-facing
// UseAcesBannersResponseActions except dismiss operates on a Notification (not a
// LocalNotification) — mergedActions below bridges that difference.
interface RawAcesActions {
  refresh: () => void;
  display: (toMark?: Notification[]) => Promise<void>;
  read: (toMark: Notification) => Promise<void>;
  readSubNotification: (toMark: string) => Promise<void>;
  dismiss: (toMark: Notification) => Promise<void>;
}

// Return tuple shape shared by useNotifications (v1) and useNotificationsV2 (v2).
type UseNotificationsReturn = readonly [
  Notification[],
  number,
  boolean,
  RawAcesActions,
];

/**
 * Builds the search params passed to the aces notifications hook. Identical for
 * both the v1 (CHFS) and v2 (WonderwallService) paths — the v2 adapter forwards
 * isGlobal/isAggregated to WWS. isGlobal is only honoured for PAGE_BANNER.
 */
const useBannerSearchParams = (placement: PlacementType): SearchParams => {
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const chromeConfiguration = useSelector(selectChromeConfiguration);
  let isGlobal = false;
  let isAggregated = false;

  if (
    placement === PlacementType.PAGE_BANNER &&
    chromeConfiguration.isAggregated &&
    chromeConfiguration.isGlobal
  ) {
    // isGlobal as true is only supported by PAGE_BANNER, any other placement type will result in an error. Use with caution.
    isGlobal = chromeConfiguration.isGlobal;
    // isAggregated flag only useful on PAGE_BANNER type. Any other types is either ignored or will result in unintended data. Change code with caution.
    isAggregated = chromeConfiguration.isAggregated;
  }

  return useMemo(
    () => ({
      ...BANNER_SEARCH_PARAMS,
      isGlobal,
      isAggregated,
      filter: {
        webpageId: chromeConfiguration.pageId,
        applicationName: internalChromeConfig?.applicationName,
      },
    }),
    [
      chromeConfiguration.pageId,
      internalChromeConfig?.applicationName,
      isGlobal,
      isAggregated,
    ],
  );
};

/**
 * Shared response shaping for both banner paths. Parses the aces v1 Notification
 * model (both paths return this model — the v2 adapter converts WWS responses
 * back into it) into LocalNotification banners and wires up the dismiss action.
 */
const useParsedAcesBanners = ([
  notifications,
  ,
  loading,
  actions,
]: UseNotificationsReturn): UseAcesBannersResponse => {
  const [localNotifications, setLocalNotifications] = useState<
    LocalNotification[]
  >([]);

  const mergedActions = useMemo(
    () => ({
      ...actions,
      dismiss: (bannerObjToUpdate) => {
        const notificationsToUpdate = notifications?.find(
          (n) => n.eventId === bannerObjToUpdate.id,
        );
        try {
          if (notificationsToUpdate) {
            return actions.dismiss(notificationsToUpdate);
          } else {
            throw new Error('Banner notification not found');
          }
        } catch (error: any) {
          UseAcesBannersMetricsPublisher.publishCounterMonitor(
            `dismissACESBannerFailure:${bannerObjToUpdate.id}`,
            1,
          );
          logger.error(
            `dismiss aces banner failed for: ${bannerObjToUpdate.id}`,
            error,
            { errorInfo: 'aces-dismiss' },
          );
        }
        return Promise.reject();
      },
    }),
    [actions, notifications],
  );

  useEffect(() => {
    if (loading) {
      return;
    }
    // only support INFORMATION, ERROR, WARNING messageType:
    // https://w.amazon.com/bin/view/Advertising_Console/Advertising_Console_Experience/Console_Banners
    // skip other messageTypes

    const parsedNotifications: LocalNotification[] = notifications
      ?.filter((item) => SUPPORTED_ACES_MSG_TYPES[item?.messageType])
      ?.map((item) => {
        const title = item.components?.find(
          (component) => component.type === 'TitleHeader',
        ) as TitleHeaderTemplate;
        const messageType = SUPPORTED_ACES_MSG_TYPES[item.messageType];
        const acesBannerContent = item.components?.filter(
          (component) => component.type === 'Text' || component.type === 'Link',
        ) as (TextTemplate | LinkTemplate)[];

        return {
          id: item.eventId,
          eventId: item.eventId, // bannerItem shape will have eventId only when data is returned from ACES API
          type: messageType,
          title: title?.text,
          acesBannerContent,
          notificationType: 'aces',
        };
      });

    setLocalNotifications(parsedNotifications);
  }, [notifications, loading]);

  return {
    notifications: localNotifications,
    actions: mergedActions,
    loading: loading,
  };
};

/**
 * Fetches banners via the legacy v1 hook (CHFS via MONS). Default path when the
 * NOTIFICATIONS_WWS_DIRECT weblab is off.
 */
export const useAcesBanners = (
  placement: PlacementType,
): UseAcesBannersResponse => {
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const searchParams = useBannerSearchParams(placement);

  const accountParams = useMemo(
    () => ({
      entityId: internalChromeConfig?.entityId,
    }),
    [internalChromeConfig?.entityId],
  );

  return useParsedAcesBanners(
    useNotifications(placement, searchParams, accountParams),
  );
};

/**
 * Fetches banners via the v2 hook, which calls WonderwallService directly
 * through the Advertising API Gateway (/a9g-api-gateway), bypassing CHFS.
 * Enabled by the NOTIFICATIONS_WWS_DIRECT weblab. The extra context fields
 * (clientId, marketplaceId, locale) required by AccountInfoParamsV2 are already
 * available on the chrome config; v2 issues CSRF inline so no token is passed.
 */
export const useAcesBannersV2 = (
  placement: PlacementType,
): UseAcesBannersResponse => {
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const searchParams = useBannerSearchParams(placement);

  const accountParams = useMemo<AccountInfoParamsV2>(
    () => ({
      entityId: internalChromeConfig?.entityId,
      clientId: CONSOLE_NOTIFICATIONS_API_CLIENT_ID,
      marketplaceId: internalChromeConfig?.marketplaceId,
      locale: internalChromeConfig?.locale,
    }),
    [
      internalChromeConfig?.entityId,
      internalChromeConfig?.marketplaceId,
      internalChromeConfig?.locale,
    ],
  );

  return useParsedAcesBanners(
    useNotificationsV2(placement, searchParams, accountParams),
  );
};
